import { Archive, Ban, MoreVertical, Pencil, RotateCcw } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { TableCell, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { formatRuDate } from '@/lib/dateFmt';
import {
  canActOnMember,
  deriveMemberStatus,
  initialsOf,
  roleLabel,
  teamCapabilities,
} from '@/pages/team/teamLogic';
import type { UserMember } from '@/pages/team/types';
import { MemberStatusBadge } from './TeamStatusBadge';

interface MemberRowProps {
  member: UserMember;
  currentUserId?: string | null;
  onChangeAccess: (m: UserMember) => void;
  onBlock: (m: UserMember) => void;
  onUnblock: (m: UserMember) => void;
  onArchive: (m: UserMember) => void;
}

function memberDisplayName(m: UserMember): string {
  return (
    m.display_name ||
    [m.first_name, m.last_name].filter(Boolean).join(' ') ||
    m.email
  );
}

function zoneLabel(m: UserMember): { main: string; sub?: string } {
  if (m.cluster_id) return { main: m.cluster_name || 'Кластер', sub: 'Кластер' };
  if (m.farm_id) return { main: m.farm_name || 'Хозяйство', sub: 'Хозяйство' };
  return { main: 'Вся компания' };
}

/** Строка участника: аватар/имя/email + роль + зона + статус + вход + меню ⋮. */
export function MemberRow({
  member,
  currentUserId,
  onChangeAccess,
  onBlock,
  onUnblock,
  onArchive,
}: MemberRowProps) {
  const status = deriveMemberStatus(member);
  const isSelf = !canActOnMember(member.user_id, currentUserId);
  const name = memberDisplayName(member);
  const zone = zoneLabel(member);
  const blocked = status === 'blocked';
  const archived = status === 'archived';
  const inactive = blocked || archived;

  return (
    <TableRow className={cn(inactive && 'opacity-60')}>
      <TableCell>
        <div className="flex items-center gap-3">
          <Avatar className="h-9 w-9 shrink-0">
            <AvatarFallback>{initialsOf(name, member.email)}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="truncate font-medium text-foreground">{name}</span>
              {isSelf && (
                <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">
                  Вы
                </Badge>
              )}
            </div>
            <div className="truncate text-xs text-muted-foreground">
              {member.email}
            </div>
          </div>
        </div>
      </TableCell>
      <TableCell className="whitespace-nowrap">
        <div className="flex items-center gap-2">
          {/* AK-367: имя роли от бэка (RBAC v2 резолвит кастомные роли);
              фолбэк — клиентский лейбл системного кода. */}
          <span className="text-muted-foreground">
            {member.role_name || roleLabel(member.role)}
          </span>
          {member.is_custom && (
            <Badge variant="outline" className="px-1.5 py-0 text-[10px]">
              Кастомная
            </Badge>
          )}
        </div>
      </TableCell>
      <TableCell>
        <div className="text-sm text-foreground">{zone.main}</div>
        {zone.sub && (
          <div className="text-xs text-muted-foreground">{zone.sub}</div>
        )}
      </TableCell>
      <TableCell>
        <MemberStatusBadge status={status} />
      </TableCell>
      <TableCell className="whitespace-nowrap text-sm tabular-nums text-muted-foreground">
        {member.last_login_at ? formatRuDate(member.last_login_at) : '—'}
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-2">
          {/* Быстрая кнопка «Разблокировать» для blocked — самое частое действие. */}
          {blocked && !isSelf && teamCapabilities.unblock && (
            <Button variant="outline" size="sm" onClick={() => onUnblock(member)}>
              <RotateCcw className="h-4 w-4" />
              Разблокировать
            </Button>
          )}
          {/* archived — насовсем, действий нет; показываем только меню «Сменить роль». */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                aria-label="Действия"
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {!archived && (
                <>
                  <DropdownMenuItem onSelect={() => onChangeAccess(member)}>
                    <Pencil className="h-4 w-4" />
                    Сменить роль / зону
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}

              {/* Блокировка / разблокировка */}
              {isSelf ? (
                <DropdownMenuItem disabled className="text-muted-foreground">
                  <Ban className="h-4 w-4" />
                  <span className="flex-1">Заблокировать</span>
                  <span className="text-xs">— это вы</span>
                </DropdownMenuItem>
              ) : blocked ? (
                <DropdownMenuItem
                  onSelect={() => onUnblock(member)}
                  disabled={!teamCapabilities.unblock}
                >
                  <RotateCcw className="h-4 w-4" />
                  Разблокировать
                </DropdownMenuItem>
              ) : archived ? null : (
                <DropdownMenuItem
                  onSelect={() => onBlock(member)}
                  disabled={!teamCapabilities.block}
                  className="text-accent-brand focus:text-accent-brand"
                >
                  <Ban className="h-4 w-4" />
                  Заблокировать
                </DropdownMenuItem>
              )}

              {/* Архивация — насовсем (active|blocked → archived) */}
              {!archived && !isSelf && (
                <DropdownMenuItem
                  onSelect={() => onArchive(member)}
                  disabled={!teamCapabilities.archive}
                  className="text-destructive focus:text-destructive"
                >
                  <Archive className="h-4 w-4" />
                  В архив
                </DropdownMenuItem>
              )}

              {archived && (
                <DropdownMenuItem disabled className="text-muted-foreground">
                  <Archive className="h-4 w-4" />
                  В архиве — насовсем
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </TableCell>
    </TableRow>
  );
}
